import { useState, useEffect } from "react";
import Topbar from "../components/layout/Topbar.jsx";
import CompanySelector from "../components/ui/CompanySelector.jsx";
import {
  getUsers,
  createUser,
  updateUser,
  toggleActive,
  resetUserPassword,
  deleteUser,
  getRoles,
  getAllPermissions,
  createRole,
  updateRolePermissions,
  deleteRole,
} from "../services/userService.js";

// ─────────────────────────────────────────────
//  GROUPES DE PERMISSIONS — pour l'affichage en sections
// ─────────────────────────────────────────────

const PERMISSION_GROUPS = [
  {
    label: "Mots de passe",
    icon:  "bi-shield-lock",
    names: [
      "CREDENTIAL_VIEW_PERSONAL",
      "CREDENTIAL_EDIT_PERSONAL",
      "CREDENTIAL_VIEW_COMPANY",
      "CREDENTIAL_COPY_COMPANY",
    ],
  },
  {
    label: "Tickets",
    icon:  "bi-ticket-detailed",
    names: ["TICKET_VIEW", "TICKET_CREATE", "TICKET_COMMENT"],
  },
  {
    label: "Parc informatique",
    icon:  "bi-pc-display",
    names: ["ASSET_VIEW"],
  },
  {
    label: "Facturation & Rapports",
    icon:  "bi-receipt",
    names: ["INVOICE_VIEW", "REPORT_VIEW"],
  },
  {
    label: "Base de connaissances",
    icon:  "bi-book",
    names: ["KB_VIEW"],
  },
];

// ─────────────────────────────────────────────
//  COMPOSANT — MODALE CRÉATION / ÉDITION UTILISATEUR
// ─────────────────────────────────────────────

function UserFormModal({ editData, companyId, roles, allPermissions, onSave, onClose }) {
  const isEdit = !!editData;

  // Rôle sélectionné — utilisé pour griser les permissions déjà incluses
  const [roleId, setRoleId] = useState(editData?.role?.id || (roles[0]?.id || ""));

  // Set des noms de permissions accordées en plus du rôle
  const [overrides, setOverrides] = useState(
    new Set((editData?.permissionOverrides || []).map(p => p.name))
  );

  const [form, setForm] = useState({
    firstName: editData?.firstName || "",
    lastName:  editData?.lastName  || "",
    email:     editData?.email     || "",
    password:  "",
  });

  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  // Permissions déjà incluses dans le rôle actuellement sélectionné
  const role = roles.find(r => r.id === roleId);
  const rolePermissionNames = new Set((role?.permissions || []).map(p => p.name));

  // Coche/décoche un override — bloqué si la permission vient déjà du rôle
  const toggleOverride = (permName) => {
    if (rolePermissionNames.has(permName)) return; // déjà incluse, rien à faire
    setOverrides(prev => {
      const next = new Set(prev);
      next.has(permName) ? next.delete(permName) : next.add(permName);
      return next;
    });
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.firstName.trim()) return setError("Le prénom est obligatoire.");
    if (!form.lastName.trim())  return setError("Le nom est obligatoire.");
    if (!form.email.trim())     return setError("L'email est obligatoire.");
    if (!roleId)                return setError("Le rôle est obligatoire.");
    // Vérification explicite — ne devrait jamais arriver si la modale
    // s'ouvre uniquement quand une entreprise est sélectionnée
    if (!companyId)             return setError("Aucune entreprise sélectionnée. Fermez et rechoisissez une entreprise.");

    setLoading(true);
    try {
      const payload = {
        firstName: form.firstName,
        lastName:  form.lastName,
        email:     form.email,
        role:      { id: roleId },
        company:   { id: companyId },
        // permissionOverrides : tableau d'objets { name } pour correspondre à l'entité Permission
        permissionOverrides: [...overrides].map(name => ({ name })),
        // À la création, aucun mot de passe n'est envoyé — le backend en
        // génère un temporaire et envoie une invitation par email.
        // En édition, le champ reste optionnel ("laisser vide pour ne pas changer").
        ...(isEdit && form.password.trim() ? { password: form.password } : {}),
      };

      if (isEdit) {
        await updateUser(editData.id, payload);
      } else {
        await createUser(payload);
      }

      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 520 }}>

        <div className="modal-header">
          <h3>{isEdit ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</h3>
          <button className="modal-close" onClick={onClose}><i className="bi bi-x-lg"/></button>
        </div>

        <div className="modal-body">
          {error && <div className="form-error"><i className="bi bi-exclamation-triangle me-2"/>{error}</div>}

          {/* Prénom + Nom */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="form-group">
              <label className="itm-label">Prénom *</label>
              <input className="itm-input" value={form.firstName}
                onChange={e => set("firstName", e.target.value)} placeholder="Alice"/>
            </div>
            <div className="form-group">
              <label className="itm-label">Nom *</label>
              <input className="itm-input" value={form.lastName}
                onChange={e => set("lastName", e.target.value)} placeholder="Dupont"/>
            </div>
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="itm-label">Email *</label>
            <input className="itm-input" type="email" value={form.email}
              onChange={e => set("email", e.target.value)} placeholder="alice@acme.fr"/>
          </div>

          {/* Mot de passe — uniquement en édition (reset optionnel).
              À la création, un mot de passe temporaire est généré et
              envoyé par email d'invitation, l'admin n'en saisit jamais. */}
          {isEdit ? (
            <div className="form-group">
              <label className="itm-label">
                Mot de passe <span style={{ color: "#9CA3AF", fontWeight: 400 }}>(laisser vide pour ne pas changer)</span>
              </label>
              <div style={{ position: "relative" }}>
                <input
                  className="itm-input" style={{ paddingRight: 44 }}
                  type={showPwd ? "text" : "password"}
                  autoComplete="new-password"
                  value={form.password}
                  onChange={e => set("password", e.target.value)}
                  placeholder="••••••••"
                />
                <button
                  type="button" onClick={() => setShowPwd(v => !v)}
                  style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF" }}
                >
                  <i className={`bi ${showPwd ? "bi-eye-slash" : "bi-eye"}`}/>
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", background: "rgba(14,165,233,.06)", border: "1px solid rgba(14,165,233,.15)", borderRadius: 10, marginBottom: 4 }}>
              <i className="bi bi-envelope-paper-fill" style={{ color: "#0EA5E9", fontSize: 15, marginTop: 1, flexShrink: 0 }} />
              <div style={{ fontSize: 12, color: "#0369A1", lineHeight: 1.6 }}>
                Un email d'invitation avec un mot de passe temporaire sera envoyé à cette adresse.
                L'utilisateur devra le changer à sa première connexion.
              </div>
            </div>
          )}

          {/* Rôle */}
          <div className="form-group">
            <label className="itm-label">Rôle *</label>
            <select className="itm-input" value={roleId} onChange={e => setRoleId(e.target.value)}>
              <option value="">— Choisir un rôle —</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            {roles.length === 0 && (
              <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>
                Aucun rôle disponible. Créez d'abord un rôle dans l'onglet "Rôles & Permissions".
              </p>
            )}
          </div>

          {/* ── Permissions supplémentaires ──
              Permet à l'admin d'accorder des droits en plus de ceux du rôle,
              spécifiquement à cet utilisateur. */}
          {roleId && (
            <div className="form-group">
              <label className="itm-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                Permissions supplémentaires
                {overrides.size > 0 && (
                  <span className="override-count-badge">+{overrides.size}</span>
                )}
              </label>
              <p style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 10 }}>
                Les permissions grisées sont déjà incluses dans le rôle "{role?.name}".
                Cochez des permissions supplémentaires pour cet utilisateur précis.
              </p>

              {PERMISSION_GROUPS.map(group => {
                const available = allPermissions.filter(p => group.names.includes(p.name));
                if (available.length === 0) return null;

                return (
                  <div key={group.label} className="perm-group">
                    <div className="perm-group-label">
                      <i className={`bi ${group.icon} me-2`}/>{group.label}
                    </div>
                    {available.map(perm => {
                      const fromRole = rolePermissionNames.has(perm.name);
                      const checked  = fromRole || overrides.has(perm.name);

                      return (
                        <label
                          key={perm.name}
                          className="perm-row"
                          style={{ opacity: fromRole ? .5 : 1, cursor: fromRole ? "default" : "pointer" }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={fromRole}
                            onChange={() => toggleOverride(perm.name)}
                            style={{ marginRight: 10, marginTop: 2 }}
                          />
                          <div>
                            <div style={{ fontSize: 13, color: "#1A1A1A" }}>
                              {perm.description}
                              {fromRole && (
                                <span style={{ fontSize: 10, color: "#9CA3AF", marginLeft: 8 }}>
                                  (inclus dans le rôle)
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "monospace" }}>{perm.name}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <><i className="bi bi-arrow-repeat spin me-1"/>Enregistrement…</> : isEdit ? "Mettre à jour" : (<><i className="bi bi-send me-1"/>Créer et inviter</>)}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  COMPOSANT — MODALE RÉINITIALISATION MOT DE PASSE
// ─────────────────────────────────────────────

function ResetPasswordModal({ user, onClose }) {
  const [pwd,     setPwd]     = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState("");

  const handleSubmit = async () => {
    if (!pwd || pwd.length < 6) return setError("Minimum 6 caractères.");
    setLoading(true);
    try {
      await resetUserPassword(user.id, pwd);
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 400 }}>

        <div className="modal-header">
          <h3>Réinitialiser le mot de passe</h3>
          <button className="modal-close" onClick={onClose}><i className="bi bi-x-lg"/></button>
        </div>

        <div className="modal-body">
          <p style={{ fontSize: 13, color: "#4B5563", marginBottom: 14 }}>
            Nouveau mot de passe pour <strong style={{ color: "#1A1A1A" }}>{user.firstName} {user.lastName}</strong>
          </p>

          {error && <div className="form-error">{error}</div>}

          {done ? (
            <div style={{ background: "rgba(105,219,124,.1)", border: "1px solid rgba(105,219,124,.3)", borderRadius: 10, padding: "12px 14px", color: "#69DB7C", fontSize: 13 }}>
              <i className="bi bi-check-circle me-2"/>Mot de passe réinitialisé avec succès.
            </div>
          ) : (
            <div className="form-group">
              <label className="itm-label">Nouveau mot de passe *</label>
              <div style={{ position: "relative" }}>
                <input
                  className="itm-input" style={{ paddingRight: 44 }}
                  type={showPwd ? "text" : "password"}
                  value={pwd} onChange={e => setPwd(e.target.value)}
                  placeholder="Minimum 6 caractères"
                />
                <button
                  type="button" onClick={() => setShowPwd(v => !v)}
                  style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF" }}
                >
                  <i className={`bi ${showPwd ? "bi-eye-slash" : "bi-eye"}`}/>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>{done ? "Fermer" : "Annuler"}</button>
          {!done && (
            <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
              {loading ? <><i className="bi bi-arrow-repeat spin me-1"/>…</> : "Réinitialiser"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  COMPOSANT — MODALE CRÉATION / ÉDITION RÔLE
// ─────────────────────────────────────────────

function RoleFormModal({ editData, companyId, allPermissions, onSave, onClose }) {
  const isEdit = !!editData;

  const [name,    setName]    = useState(editData?.name || "");
  const [selected, setSelected] = useState(
    new Set(editData?.permissions?.map(p => p.name) || [])
  );
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  // Coche ou décoche une permission
  const toggle = (permName) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(permName) ? next.delete(permName) : next.add(permName);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!name.trim()) return setError("Le nom du rôle est obligatoire.");
    setLoading(true);
    try {
      const permissionNames = [...selected];

      if (isEdit) {
        // Modification : on met à jour les permissions
        await updateRolePermissions(editData.id, permissionNames);
      } else {
        // Création : companyId à plat pour correspondre au record CreateRoleRequest du backend
        await createRole({
          name,
          companyId,
          permissionNames,
        });
      }

      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 520 }}>

        <div className="modal-header">
          <h3>{isEdit ? `Modifier "${editData.name}"` : "Nouveau rôle"}</h3>
          <button className="modal-close" onClick={onClose}><i className="bi bi-x-lg"/></button>
        </div>

        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}

          {/* Nom du rôle — désactivé en édition */}
          <div className="form-group">
            <label className="itm-label">Nom du rôle *</label>
            <input
              className="itm-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ex: Responsable IT"
              disabled={isEdit}
              style={isEdit ? { opacity: .6 } : {}}
            />
          </div>

          {/* Permissions groupées par module */}
          <label className="itm-label" style={{ marginBottom: 10, display: "block" }}>
            Permissions ({selected.size} sélectionnée{selected.size > 1 ? "s" : ""})
          </label>

          {PERMISSION_GROUPS.map(group => {
            // On ne montre que les permissions qui existent en base
            const available = allPermissions.filter(p => group.names.includes(p.name));
            if (available.length === 0) return null;

            return (
              <div key={group.label} className="perm-group">
                <div className="perm-group-label">
                  <i className={`bi ${group.icon} me-2`}/>{group.label}
                </div>
                {available.map(perm => (
                  <label key={perm.name} className="perm-row">
                    <input
                      type="checkbox"
                      checked={selected.has(perm.name)}
                      onChange={() => toggle(perm.name)}
                      style={{ marginRight: 10 }}
                    />
                    <div>
                      <div style={{ fontSize: 13, color: "#1A1A1A" }}>{perm.description}</div>
                      <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "monospace" }}>{perm.name}</div>
                    </div>
                  </label>
                ))}
              </div>
            );
          })}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <><i className="bi bi-arrow-repeat spin me-1"/>Enregistrement…</> : isEdit ? "Mettre à jour" : "Créer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────

/**
 * Page de gestion des utilisateurs et des rôles.
 *
 * L'admin sélectionne d'abord une entreprise via CompanySelector.
 * Ensuite les users, rôles et permissions se chargent.
 */
export default function UsersPage() {

  // Entreprise sélectionnée — null tant que rien n'est choisi
  const [company, setCompany] = useState(null);

  const companyId   = company?.id   || null;
  const companyName = company?.name || null;

  // ── Onglet actif : "users" ou "roles" ────────
  const [activeTab, setActiveTab] = useState("users");

  // ── Données ──────────────────────────────────
  const [users,          setUsers]          = useState([]);
  const [roles,          setRoles]          = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState("");

  // ── Modales ───────────────────────────────────
  const [showUserForm,   setShowUserForm]   = useState(false);
  const [editUser,       setEditUser]       = useState(null);
  const [resetUser,      setResetUser]      = useState(null);
  const [showRoleForm,   setShowRoleForm]   = useState(false);
  const [editRole,       setEditRole]       = useState(null);

  useEffect(() => {
    if (companyId) loadAll();
  }, [companyId]);

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      // Chargement en parallèle
      const [usersRes, rolesRes, permsRes] = await Promise.all([
        getUsers(companyId),
        getRoles(companyId),
        getAllPermissions(),
      ]);
      setUsers(usersRes.data          || []);
      setRoles(rolesRes.data          || []);
      setAllPermissions(permsRes.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Activation / désactivation d'un user
  const handleToggleActive = async (user) => {
    try {
      await toggleActive(user.id, !user.active);
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, active: !user.active } : u));
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  // Suppression d'un user
  const handleDeleteUser = async (id) => {
    if (!window.confirm("Supprimer définitivement cet utilisateur ?")) return;
    try {
      await deleteUser(id);
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  // Suppression d'un rôle
  const handleDeleteRole = async (id) => {
    if (!window.confirm("Supprimer ce rôle ?")) return;
    try {
      await deleteRole(id);
      setRoles(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      alert(err.message); // peut contenir "role est assigné à N utilisateurs"
    }
  };

  // ── Cas : aucune entreprise sélectionnée ──────
  if (!companyId) {
    return (
      <>
        <Topbar title="Utilisateurs & Rôles" subtitle="Administration"/>
        <div className="page-body">
          <div className="cs-page-selector">
            <i className="bi bi-people" style={{ fontSize: 40, opacity: .3, marginBottom: 16 }}/>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#4B5563", marginBottom: 8 }}>
              Sélectionnez une entreprise
            </p>
            <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 20 }}>
              Choisissez une entreprise pour gérer ses utilisateurs et ses rôles.
            </p>
            <CompanySelector
              selectedId={companyId}
              onChange={setCompany}
            />
          </div>
        </div>
        <style>{CSS}</style>
      </>
    );
  }

  return (
    <>
      <Topbar title="Utilisateurs & Rôles" subtitle={companyName || "Administration"}/>
      <div className="page-body">

        {/* ── En-tête : sélecteur d'entreprise + onglets + bouton Ajouter ── */}
        <div className="pwd-header anim-fade-up">
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, flexWrap: "wrap" }}>

            {/* Sélecteur d'entreprise — permet de changer sans recharger la page */}
            <CompanySelector
              selectedId={companyId}
              onChange={(c) => {
                setCompany(c);
                // Réinitialise les données quand on change d'entreprise
                setUsers([]);
                setRoles([]);
              }}
            />

            <div className="pwd-tabs">
              <button
                className={`pwd-tab${activeTab === "users" ? " active" : ""}`}
                onClick={() => setActiveTab("users")}
              >
                <i className="bi bi-people-fill me-2"/>
                Utilisateurs
                <span className="pwd-tab-count">{users.length}</span>
              </button>
              <button
                className={`pwd-tab${activeTab === "roles" ? " active" : ""}`}
                onClick={() => setActiveTab("roles")}
              >
                <i className="bi bi-shield-half me-2"/>
                Rôles & Permissions
                <span className="pwd-tab-count">{roles.length}</span>
              </button>
            </div>
          </div>

          {/* Bouton Ajouter contextuel selon l'onglet */}
          {activeTab === "users" && (
            <button className="pwd-add-btn" onClick={() => { setEditUser(null); setShowUserForm(true); }}>
              <i className="bi bi-person-plus me-1"/>Ajouter un utilisateur
            </button>
          )}
          {activeTab === "roles" && (
            <button className="pwd-add-btn" onClick={() => { setEditRole(null); setShowRoleForm(true); }}>
              <i className="bi bi-plus-lg me-1"/>Nouveau rôle
            </button>
          )}
        </div>

        {/* ── Chargement ── */}
        {loading && (
          <div className="pwd-empty">
            <i className="bi bi-arrow-repeat spin" style={{ fontSize: 32, opacity: .4 }}/>
            <p>Chargement…</p>
          </div>
        )}

        {/* ── Erreur ── */}
        {!loading && error && (
          <div className="pwd-empty">
            <i className="bi bi-exclamation-triangle" style={{ color: "#DC2626", opacity: 1 }}/>
            <p style={{ color: "#DC2626" }}>{error}</p>
            <button className="pwd-add-btn" onClick={loadAll} style={{ marginTop: 10 }}>Réessayer</button>
          </div>
        )}

        {/* ═════════════════════════════════════════
            ONGLET UTILISATEURS
        ═════════════════════════════════════════ */}
        {!loading && !error && activeTab === "users" && (
          <div className="itm-card anim-fade-up" style={{ padding: 0 }}>

            {/* En-tête tableau */}
            <div className="user-table-header">
              <span>Utilisateur</span>
              <span>Email</span>
              <span>Rôle</span>
              <span>Statut</span>
              <span/>
            </div>

            {users.length === 0 && (
              <div className="pwd-empty">
                <i className="bi bi-person-x"/>
                <p>Aucun utilisateur</p>
                <small>Créez le premier utilisateur pour cette entreprise.</small>
              </div>
            )}

            {users.map((user, idx) => (
              <div
                key={user.id}
                className="user-row"
                style={{ borderTop: idx > 0 ? "1px solid rgba(0,0,0,.08)" : "none" }}
              >
                {/* Avatar + nom */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div className="avatar-sm">
                    {user.firstName?.[0]}{user.lastName?.[0]}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1A1A1A" }}>
                      {user.firstName} {user.lastName}
                    </div>
                  </div>
                </div>

                {/* Email */}
                <span style={{ fontSize: 12, color: "#4B5563" }}>{user.email}</span>

                {/* Rôle */}
                <span className="role-badge">{user.role?.name || "—"}</span>

                {/* Statut */}
                <span className={`status-badge ${user.active ? "status-active" : "status-inactive"}`}>
                  <i className={`bi ${user.active ? "bi-check-circle" : "bi-x-circle"} me-1`}/>
                  {user.active ? "Actif" : "Désactivé"}
                </span>

                {/* Actions */}
                <div className="cred-actions">
                  {/* Modifier */}
                  <button className="cred-action-btn"
                    onClick={() => { setEditUser(user); setShowUserForm(true); }}
                    title="Modifier">
                    <i className="bi bi-pencil"/>
                  </button>

                  {/* Réinitialiser le mot de passe */}
                  <button className="cred-action-btn"
                    onClick={() => setResetUser(user)}
                    title="Réinitialiser le mot de passe">
                    <i className="bi bi-key"/>
                  </button>

                  {/* Activer / Désactiver */}
                  <button
                    className={`cred-action-btn ${user.active ? "warning" : "success"}`}
                    onClick={() => handleToggleActive(user)}
                    title={user.active ? "Désactiver" : "Activer"}
                  >
                    <i className={`bi ${user.active ? "bi-person-slash" : "bi-person-check"}`}/>
                  </button>

                  {/* Supprimer */}
                  <button className="cred-action-btn danger"
                    onClick={() => handleDeleteUser(user.id)}
                    title="Supprimer">
                    <i className="bi bi-trash"/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ═════════════════════════════════════════
            ONGLET RÔLES
        ═════════════════════════════════════════ */}
        {!loading && !error && activeTab === "roles" && (
          <div className="itm-card anim-fade-up" style={{ padding: 0 }}>

            {roles.length === 0 && (
              <div className="pwd-empty">
                <i className="bi bi-shield"/>
                <p>Aucun rôle</p>
                <small>Créez des rôles pour contrôler les accès des utilisateurs.</small>
              </div>
            )}

            {roles.map((role, idx) => (
              <div
                key={role.id}
                className="role-row"
                style={{ borderTop: idx > 0 ? "1px solid rgba(0,0,0,.08)" : "none" }}
              >
                {/* Nom du rôle + nombre d'utilisateurs */}
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#1A1A1A" }}>{role.name}</div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 3 }}>
                    {role.users?.length || 0} utilisateur{role.users?.length !== 1 ? "s" : ""}
                  </div>
                </div>

                {/* Badges des permissions */}
                <div className="perm-badges">
                  {(role.permissions || []).slice(0, 5).map(p => (
                    <span key={p.name} className="perm-badge" title={p.description}>
                      {p.name.split("_").slice(-1)[0]}
                    </span>
                  ))}
                  {(role.permissions || []).length > 5 && (
                    <span className="perm-badge">+{role.permissions.length - 5}</span>
                  )}
                  {(role.permissions || []).length === 0 && (
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>Aucune permission</span>
                  )}
                </div>

                {/* Actions */}
                <div className="cred-actions">
                  <button className="cred-action-btn"
                    onClick={() => { setEditRole(role); setShowRoleForm(true); }}
                    title="Modifier les permissions">
                    <i className="bi bi-pencil"/>
                  </button>
                  <button className="cred-action-btn danger"
                    onClick={() => handleDeleteRole(role.id)}
                    title="Supprimer">
                    <i className="bi bi-trash"/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Modales ── */}
      {showUserForm && (
        <UserFormModal
          editData={editUser}
          companyId={companyId}
          roles={roles}
          allPermissions={allPermissions}
          onSave={() => { setShowUserForm(false); setEditUser(null); loadAll(); }}
          onClose={() => { setShowUserForm(false); setEditUser(null); }}
        />
      )}

      {resetUser && (
        <ResetPasswordModal
          user={resetUser}
          onClose={() => setResetUser(null)}
        />
      )}

      {showRoleForm && (
        <RoleFormModal
          editData={editRole}
          companyId={companyId}
          allPermissions={allPermissions}
          onSave={() => { setShowRoleForm(false); setEditRole(null); loadAll(); }}
          onClose={() => { setShowRoleForm(false); setEditRole(null); }}
        />
      )}

      <style>{CSS}</style>
    </>
  );
}

// ─────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────

const CSS = `
/* Sélecteur centré (état vide) */
.cs-page-selector{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:80px 20px;text-align:center}

/* En-tête */
.pwd-header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:14px;flex-wrap:wrap}
.pwd-add-btn{display:flex;align-items:center;gap:7px;background:#E63329;border:none;border-radius:10px;color:#fff;font-size:13px;font-weight:600;padding:9px 16px;cursor:pointer;transition:filter .18s}
.pwd-add-btn:hover{filter:brightness(1.12)}

/* Onglets */
.pwd-tabs{display:flex;gap:4px;background:#F4F5F7;border:1px solid rgba(0,0,0,.08);border-radius:10px;padding:4px}
.pwd-tab{display:flex;align-items:center;gap:6px;background:none;border:none;border-radius:7px;color:#4B5563;font-size:13px;padding:7px 16px;cursor:pointer;transition:all .15s;white-space:nowrap}
.pwd-tab:hover{color:#1A1A1A;background:rgba(0,0,0,.04)}
.pwd-tab.active{background:rgba(230,51,41,.1);color:#E63329;font-weight:600}
.pwd-tab-count{background:rgba(0,0,0,.08);border-radius:20px;font-size:11px;font-weight:600;padding:1px 7px;margin-left:2px}
.pwd-tab.active .pwd-tab-count{background:#E63329;color:#fff}

/* Tableau utilisateurs */
.user-table-header{display:grid;grid-template-columns:2fr 2fr 1fr 1fr auto;gap:12px;padding:8px 16px;font-size:10px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#9CA3AF;border-bottom:1px solid rgba(0,0,0,.08)}
.user-row{display:grid;grid-template-columns:2fr 2fr 1fr 1fr auto;gap:12px;padding:12px 16px;align-items:center}
.user-row:hover{background:#F9F9FA}

/* Avatar */
.avatar-sm{width:32px;height:32px;border-radius:50%;background:linear-gradient(135deg,#c92b22,#E63329);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#fff;text-transform:uppercase;flex-shrink:0}

/* Badges */
.role-badge{font-size:11px;font-weight:600;padding:3px 10px;border-radius:20px;background:rgba(103,65,217,.1);color:#6741D9;border:1px solid rgba(103,65,217,.25);white-space:nowrap}
.status-badge{font-size:11px;font-weight:600;padding:3px 10px;border-radius:20px;white-space:nowrap}
.status-active{background:rgba(47,158,68,.1);color:#2F9E44;border:1px solid rgba(47,158,68,.25)}
.status-inactive{background:rgba(239,68,68,.1);color:#DC2626;border:1px solid rgba(239,68,68,.25)}

/* Boutons d'action */
.cred-actions{display:flex;gap:4px}
.cred-action-btn{width:28px;height:28px;border-radius:7px;background:none;border:1px solid transparent;color:#9CA3AF;font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all .15s}
.cred-action-btn:hover{background:rgba(0,0,0,.08);color:#1A1A1A}
.cred-action-btn.danger:hover{background:rgba(220,38,38,.1);color:#DC2626}
.cred-action-btn.warning:hover{background:rgba(245,158,11,.1);color:#f59e0b}
.cred-action-btn.success:hover{background:rgba(47,158,68,.1);color:#2F9E44}

/* Tableau rôles */
.role-row{display:grid;grid-template-columns:1fr 2fr auto;gap:12px;padding:14px 16px;align-items:center}
.role-row:hover{background:#F9F9FA}
.perm-badges{display:flex;gap:4px;flex-wrap:wrap}
.perm-badge{font-size:10px;font-weight:600;padding:2px 7px;border-radius:6px;background:rgba(0,0,0,.06);color:#4B5563;white-space:nowrap}

/* Formulaire de rôle */
.perm-group{background:#F9F9FA;border:1px solid rgba(0,0,0,.07);border-radius:10px;padding:12px 14px;margin-bottom:10px}
.perm-group-label{font-size:11px;font-weight:700;color:#4B5563;text-transform:uppercase;letter-spacing:.08em;margin-bottom:10px}
.perm-row{display:flex;align-items:flex-start;gap:0;padding:6px 0;cursor:pointer;border-bottom:1px solid rgba(0,0,0,.05)}
.perm-row:last-child{border-bottom:none}
.perm-row:hover{opacity:.75}

/* Modales (partagées avec AdminPasswordsPage) */
.modal-overlay{position:fixed;inset:0;z-index:1055;background:rgba(26,26,26,.6);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:16px;animation:fadeIn .15s ease}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
.modal-box{background:#fff;border:1px solid rgba(0,0,0,.08);border-radius:18px;box-shadow:0 40px 120px rgba(0,0,0,.25);width:100%;max-width:560px;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;animation:slideIn .2s cubic-bezier(.34,1.56,.64,1)}
@keyframes slideIn{from{transform:translateY(24px);opacity:0}to{transform:none;opacity:1}}
.modal-header{display:flex;align-items:center;justify-content:space-between;padding:18px 20px 14px;border-bottom:1px solid rgba(0,0,0,.08)}
.modal-header h3{font-size:15px;font-weight:700;color:#1A1A1A;margin:0}
.modal-close{background:#F4F5F7;border:none;cursor:pointer;color:#4B5563;width:28px;height:28px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:12px}
.modal-close:hover{background:#E63329;color:#fff}
.modal-body{padding:18px 20px;overflow-y:auto;flex:1;display:flex;flex-direction:column;gap:0}
.modal-footer{padding:14px 20px;border-top:1px solid rgba(0,0,0,.08);display:flex;justify-content:flex-end;gap:10px}

/* Formulaire */
.form-group{margin-bottom:14px}
.form-error{background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.25);border-radius:8px;color:#DC2626;font-size:12px;padding:8px 12px;margin-bottom:14px}
.btn-primary{background:#E63329;border:none;border-radius:10px;color:#fff;font-size:13px;font-weight:600;padding:8px 18px;cursor:pointer;display:flex;align-items:center;gap:6px;transition:filter .15s}
.btn-primary:hover{filter:brightness(1.1)}
.btn-primary:disabled{opacity:.5;cursor:not-allowed}
.btn-secondary{background:#fff;border:1px solid rgba(0,0,0,.1);border-radius:10px;color:#4B5563;font-size:13px;padding:8px 18px;cursor:pointer;transition:all .15s}
.btn-secondary:hover{background:#F4F5F7;color:#1A1A1A}

/* Vide */
.pwd-empty{display:flex;flex-direction:column;align-items:center;padding:60px 20px;color:#9CA3AF;text-align:center}
.pwd-empty i{font-size:42px;margin-bottom:14px;opacity:.4}
.pwd-empty p{font-size:14px;font-weight:500;color:#4B5563;margin:0 0 4px}
.pwd-empty small{font-size:12px}

/* Animations */
@keyframes spin{to{transform:rotate(360deg)}}
.spin{animation:spin .7s linear infinite;display:inline-block}
`;